import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { templatesApi } from '../api/templates';

interface Props {
  templateType: string;
  /** Optional filter so only templates saved for this subtype are shown/selectable (e.g. league type). */
  subtype?: string;
  currentData: Record<string, unknown>;
  onApply: (data: Record<string, unknown>) => void;
  /** Hide the "apply template" dropdown, e.g. when this bar is only used for saving. */
  hideApply?: boolean;
  /** Hide the "save as template" control, e.g. when this bar is only used for applying. */
  hideSave?: boolean;
}

export default function TemplateBar({ templateType, subtype, currentData, onApply, hideApply, hideSave }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const { data: templates = [] } = useQuery({
    queryKey: ['templates', templateType],
    queryFn: () => templatesApi.list(templateType).then((r) => r.data),
  });

  const visibleTemplates = templates.filter((tpl) => !subtype || tpl.data.__subtype === subtype);

  const saveMutation = useMutation({
    mutationFn: () => templatesApi.create(templateType, saveName.trim(), { ...currentData, __subtype: subtype }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', templateType] });
      setSaveOpen(false);
      setSaveName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => templatesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates', templateType] });
      setSelected('');
    },
  });

  function handleSelect(id: string) {
    setSelected(id);
    const tpl = visibleTemplates.find((tp) => String(tp.id) === id);
    if (tpl) onApply(tpl.data);
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      {!hideApply && (
        <>
          <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{t('templates.label')}</span>
          <select
            value={selected}
            onChange={(e) => handleSelect(e.target.value)}
            className="input py-1.5 text-sm max-w-[220px]"
          >
            <option value="">{t('templates.choose')}</option>
            {visibleTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
          {selected && (
            <button
              type="button"
              onClick={() => deleteMutation.mutate(Number(selected))}
              disabled={deleteMutation.isPending}
              className="text-red-500 hover:text-red-600 text-xs font-medium"
            >
              {t('templates.delete')}
            </button>
          )}
        </>
      )}

      {!hideSave && (
        <div className="ml-auto flex items-center gap-2">
          {saveOpen ? (
            <>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className="input py-1.5 text-sm"
                placeholder={t('templates.namePlaceholder')}
                autoFocus
              />
              <button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={!saveName.trim() || saveMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {t('templates.save')}
              </button>
              <button type="button" onClick={() => setSaveOpen(false)} className="btn btn-secondary btn-sm">
                {t('templates.cancel')}
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setSaveOpen(true)} className="btn btn-secondary btn-sm">
              {t('templates.saveCurrent')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
